import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { formatGameTypeName, getGameTypeEmoji } from '../utils/gameResultsService';

interface DailyResult {
  id: string;
  game_type: 'sum_grid' | 'multiplication_grid' | 'combined_grid';
  completion_time_seconds: number;
  hints_used: number;
  accuracy: number;
  challenge_date: string;
  completed_at: string;
}

const DailyResultsAnalytics = () => {
  const { user } = useAuth();
  const [dailyResults, setDailyResults] = useState<DailyResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterByGame, setFilterByGame] = useState<string>('all');

  useEffect(() => {
    if (user) {
      loadDailyResults();
    }
  }, [user]);

  const loadDailyResults = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('daily_grid_results')
        .select('*')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false })
        .limit(25); // Last 25 game sessions

      if (error) {
        console.error('Error loading daily results:', error);
        return;
      }

      setDailyResults(data || []);
    } catch (error) {
      console.error('Error loading daily results:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (dailyResults.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-4xl mb-4">🎮</div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          No game sessions yet
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Complete some daily challenges to see your results here!
        </p>
      </div>
    );
  }

  const filteredResults = dailyResults.filter(result => 
    filterByGame === 'all' || result.game_type === filterByGame
  );

  return (
    <div className="space-y-4">
      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilterByGame('all')}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
            filterByGame === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          All Games ({dailyResults.length})
        </button>
        {[
          { key: 'sum_grid', name: 'Sum Grid', emoji: '➕' },
          { key: 'multiplication_grid', name: 'Multiplication', emoji: '✖️' },
          { key: 'combined_grid', name: 'Combined', emoji: '🔢' }
        ].map(game => {
          const count = dailyResults.filter(r => r.game_type === game.key).length;
          if (count === 0) return null;
          
          return (
            <button
              key={game.key}
              onClick={() => setFilterByGame(game.key)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filterByGame === game.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {game.emoji} {game.name} ({count})
            </button>
          );
        })}
      </div>

      {/* Game Sessions List */}
      <div className="space-y-3">
        {filteredResults.map((result) => (
          <div
            key={result.id}
            className="bg-white/50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200/50 dark:border-gray-600/50"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="text-2xl">{getGameTypeEmoji(result.game_type)}</div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    {formatGameTypeName(result.game_type)}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(result.completed_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-6 text-sm">
                <div className="text-center">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {formatTime(result.completion_time_seconds)}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">Time</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DailyResultsAnalytics;